import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import { QUERY_KEY_PORTAL_GET_COMPANY, useGetCompany } from '@apps-common/apis/portal/company'
import { useUpdateCompany } from '@apps-common/apis/portal/company/update-company'
import { ButtonRounded } from '@apps-common/components/elements/button'
import { InputSM } from '@apps-common/components/elements/form/InputSM'
import { ModalBody, ModalContainer, ModalFooter, ModalHeader, ModalHeaderContainer } from '@apps-common/components/modals/modal'
import { NavbarIcon } from '@apps-common/components/navigation'
import { ModalDismiss } from '@apps-common/config/modal'
import { EMAIL_VALIDATION_REGEX, PHONE_VALIDATION_REGEX } from '@apps-common/config/regex'
import { queryClient } from '@apps-common/lib'
import { ICompanyModel } from '@apps-common/types/portal/company'

export const CompanyDetailsUpdateModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  const { data: company, isLoading: isLoadingCompany, isError: isErrorCompany, isRefetching: isRefetchingCompany } = useGetCompany({})
  const { mutate: updateCompany, isPending: updateCompanyIsPending } = useUpdateCompany(true, () => {
    onDismiss(null, ModalDismiss.Confirm)
    queryClient.invalidateQueries({ queryKey: [QUERY_KEY_PORTAL_GET_COMPANY] })
  })

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isDirty },
    reset
  } = useForm<ICompanyModel>({
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  useEffect(() => {
    if (company) {
      reset(company)
    }
  }, [company])

  const onSubmit = (values: ICompanyModel) => updateCompany({ model: values })

  const loading = isLoadingCompany || isErrorCompany
  const updating = isSubmitting || updateCompanyIsPending || isRefetchingCompany

  return (
    <ModalContainer innerClasses='!h-auto !min-h-0'>
      <ModalHeaderContainer>
        <div className='flex items-start justify-between'>
          <ModalHeader subtitle='Update' title='Company details' />
          <div className='flex items-center justify-end gap-[15px]'>
            <NavbarIcon icon='close' disabled={loading || updating} callback={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='!pt-[100px]'>
        <form id='companyDetailsUpdateForm' noValidate onSubmit={handleSubmit(onSubmit)}>
          <InputSM
            type='text'
            placeholder='Enter your company name'
            label='Company name'
            id='companyName'
            showLabel
            containerClassName='mb-4'
            info='Company name is connected to your portal account cannot currently be updated.'
            loading={loading}
            disabled
            registration={register('companyName', { required: true })}
            errors={errors.companyName}
          />
          <InputSM
            type='text'
            placeholder='Enter your company display name'
            label='Company display name'
            id='displayName'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('displayName', { required: false })}
            errors={errors.displayName}
          />
          <InputSM
            type='text'
            placeholder='Enter your company email'
            label='Email'
            id='email'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('email', {
              required: true,
              pattern: {
                value: EMAIL_VALIDATION_REGEX,
                message: 'Enter a valid email'
              }
            })}
            errors={errors.email}
          />
          <InputSM
            type='text'
            placeholder='Enter your company contact phone'
            label='Phone'
            id='yourPhone'
            showLabel
            loading={loading}
            disabled={updating}
            registration={register('phone', {
              required: true,
              pattern: {
                value: PHONE_VALIDATION_REGEX,
                message: 'Enter a valid NZ phone number'
              }
            })}
            errors={errors.phone}
          />
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='w-full flex items-center justify-start gap-2'>
          <ButtonRounded
            form='companyDetailsUpdateForm'
            type='submit'
            loading={updating}
            text='Save'
            className='!h-[30px] min-h-0 !min-w-[120px] mt-0 !w-auto'
          />
          <ButtonRounded
            type='button'
            loading={updating}
            text='Cancel'
            className='!h-[30px] min-h-0 !min-w-[120px] mt-0 !w-auto'
            callback={() => onDismiss(null, 'cancel')}
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
